<template>
  <component
    :is="cardComponent"
    v-if="cardComponent && historyItem"
    v-model:pick-up-call-target-id="pickUpCallTargetId"
    :call-target-id="callTargetId"
    :call-target="withCallTarget ? callTarget : undefined"
    :editable="editable"
    :expand="expand"
    :data-test="`${historyItem.type}-${historyItem.id || ''}`"
    v-bind="cardProps.attributes"
    v-on="cardProps.events"
  />
</template>

<script setup lang="ts">
import { type Component, computed } from 'vue';
import type { HistoryCommon, HistoryCommonTypeEnum, CallTargetMinimum } from '@/api/openapi';

// Import all card components
import BoxilLeadDownloadCard from '@/components/organisms/user/lead/history/BoxilLeadDownloadCard.vue';
import BoxilLeadSurveyCard from '@/components/organisms/user/lead/history/BoxilLeadSurveyCard.vue';
import CallResultCard from '@/components/organisms/user/lead/history/CallResultCard.vue';
import FreeActionCard from '@/components/organisms/user/lead/history/FreeActionCard.vue';
import LeadStageCard from '@/components/organisms/user/lead/history/LeadStageCard.vue';
import MailCard from '@/components/organisms/user/lead/history/MailCard.vue';
import MailEventCard from '@/components/organisms/user/lead/history/MailEventCard.vue';
import NextActionCard from '@/components/organisms/user/lead/history/NextActionCard.vue';
import OpportunityCard from '@/components/organisms/user/lead/history/OpportunityCard.vue';
import SequenceInstanceCard from '@/components/organisms/user/lead/history/SequenceInstanceCard.vue';
import SequenceStepInstanceCard from '@/components/organisms/user/lead/history/SequenceStepInstanceCard.vue';
import UnsubscribedCard from '@/components/organisms/user/lead/history/UnsubscribedCard.vue';

type THistoryItem = HistoryCommon & { id?: number };

type TProps = {
  historyItem: THistoryItem;
  callTargetId: number;
  callTarget: CallTargetMinimum;
  withCallTarget?: boolean;
  editable?: boolean;
  expand?: boolean;
  isZoomPhoneConnected?: boolean;
  // Event handlers
  functions?: {
    callResult: Record<string, (...args: unknown[]) => void>;
    nextAction: Record<string, (...args: unknown[]) => void>;
    opportunity: Record<string, (...args: unknown[]) => void>;
    freeAction: Record<string, (...args: unknown[]) => void>;
    mail: {
      replyToReceivedMail: (...args: unknown[]) => void;
      replyToSentDirectMail: (...args: unknown[]) => void;
    };
  };
};

const props = withDefaults(defineProps<TProps>(), {
  withCallTarget: false,
  editable: undefined,
  expand: undefined,
  isZoomPhoneConnected: undefined,
  functions: undefined,
});

// v-model for pickUpCallTargetId to pass through to parent
const pickUpCallTargetId = defineModel<number>('pickUpCallTargetId');

const componentMap: Record<HistoryCommonTypeEnum, Component> = {
  callResult: CallResultCard,
  nextAction: NextActionCard,
  opportunity: OpportunityCard,
  freeAction: FreeActionCard,
  sendMailBulk: MailCard,
  sendMailDirect: MailCard,
  receiveMail: MailCard,
  unsubscribeCallTarget: UnsubscribedCard,
  sendgridEvent: MailEventCard,
  boxilLeadSurvey: BoxilLeadSurveyCard,
  boxilLeadDownload: BoxilLeadDownloadCard,
  leadStageHistory: LeadStageCard,
  sequenceInstanceHistory: SequenceInstanceCard,
  sequenceStepInstanceHistory: SequenceStepInstanceCard,
} as const;

const cardComponent = computed(() => {
  return componentMap[props.historyItem.type];
});

const cardProps = computed(() => {
  switch (props.historyItem.type) {
    case 'callResult':
      return {
        attributes: {
          callResult: props.historyItem,
          isZoomPhoneConnected: props.isZoomPhoneConnected,
        },
        events: props.functions?.callResult || {},
      };
    
    case 'nextAction':
      return {
        attributes: {
          nextAction: props.historyItem,
        },
        events: props.functions?.nextAction || {},
      };
    
    case 'opportunity':
      return {
        attributes: {
          opportunity: props.historyItem,
          opportunityCallTarget: props.callTarget,
        },
        events: props.functions?.opportunity || {},
      };
    
    case 'freeAction':
      return {
        attributes: {
          freeAction: props.historyItem,
        },
        events: props.functions?.freeAction || {},
      };
    
    case 'sendMailBulk':
    case 'sendMailDirect':
    case 'receiveMail':
      return {
        attributes: {
          mail: props.historyItem,
        },
        events: props.functions?.mail || {},
      };
    
    case 'unsubscribeCallTarget':
      return {
        attributes: {
          unsubscribeItem: props.historyItem,
        },
        events: {},
      };
    
    case 'sendgridEvent':
      return {
        attributes: {
          sendgridEvent: props.historyItem,
        },
        events: {},
      };
    
    case 'boxilLeadSurvey':
      return {
        attributes: {
          boxilLeadSurvey: props.historyItem,
        },
        events: {},
      };
    
    case 'boxilLeadDownload':
      return {
        attributes: {
          boxilLeadDownload: props.historyItem,
        },
        events: {},
      };
    
    case 'leadStageHistory':
      return {
        attributes: {
          leadStageHistory: props.historyItem,
        },
        events: {},
      };
    
    case 'sequenceInstanceHistory':
      return {
        attributes: {
          sequenceInstanceHistory: props.historyItem,
        },
        events: {},
      };
    
    case 'sequenceStepInstanceHistory':
      return {
        attributes: {
          sequenceStepInstanceHistory: props.historyItem,
        },
        events: {},
      };
    
    default:
      return {
        attributes: {},
        events: {},
      };
  }
});
</script>
